"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UserMerchantItemSchema = exports.deleteAccountResponseSchema = exports.getUserMerchantsResponseSchema = exports.updateSettingsResponseSchema = exports.updateSettingsBodySchema = exports.getSettingsResponseSchema = exports.updateProfileResponseSchema = exports.updateProfileBodySchema = exports.getProfileResponseSchema = exports.UserFcmTokenEntitySchema = exports.UserEntitySchema = exports.DeviceTypeSchema = exports.LanguageSchema = exports.UserRoleSchema = void 0;
const zod_1 = require("zod");
const common_schemas_1 = require("../base/common.schemas");
const api_schemas_1 = require("../base/api.schemas");
const membership_schemas_1 = require("./membership.schemas");
const timestamp_schemas_1 = require("../base/timestamp.schemas");
// ============================================
// INTERNAL SCHEMAS (Entity)
// ============================================
// System-wide user roles (from users.user_role column)
// Note: Merchant roles (admin, staff, member) are in user_merchants.role, not here
const _UserRoleSchema = zod_1.z.enum(['user', 'super_admin']);
// Language preferences (from database CHECK constraint)
const _LanguageSchema = zod_1.z.enum(['en', 'fr', 'zh']);
// User base schema matching database users table
const _UserBaseSchema = zod_1.z.object({
    id: common_schemas_1.uuidSchema, // UUID, links to auth.users
    phone_number: zod_1.z.string().max(20).nullable(), // E.164 format
    display_name: zod_1.z.string().max(100).nullable(),
    email: zod_1.z.string().email().max(255).nullable(), // For merchants using email auth
    preferred_language: _LanguageSchema.default('fr'),
    user_role: _UserRoleSchema.default('user'),
    is_active: zod_1.z.boolean().default(true),
    created_at: timestamp_schemas_1.timestampIsoSchema,
    updated_at: timestamp_schemas_1.timestampIsoSchema,
});
// Device type (from user_fcm_tokens.device_type CHECK constraint)
const _DeviceTypeSchema = zod_1.z.enum(['ios', 'android']);
// User FCM token entity schema matching database user_fcm_tokens table
const _UserFcmTokenBaseSchema = zod_1.z.object({
    token_id: zod_1.z.number().int().positive(),
    user_id: common_schemas_1.uuidSchema,
    fcm_token: zod_1.z.string().max(255),
    device_type: _DeviceTypeSchema,
    created_at: timestamp_schemas_1.timestampIsoSchema,
    last_used_at: timestamp_schemas_1.timestampIsoSchema,
});
// ============================================
// ENTITY SCHEMAS (Export Aliases)
// ============================================
exports.UserRoleSchema = _UserRoleSchema;
exports.LanguageSchema = _LanguageSchema;
exports.DeviceTypeSchema = _DeviceTypeSchema;
exports.UserEntitySchema = _UserBaseSchema;
exports.UserFcmTokenEntitySchema = _UserFcmTokenBaseSchema;
// ============================================
// CUSTOMER PROFILE API SCHEMAS
// ============================================
// GET /profile - Response
exports.getProfileResponseSchema = (0, api_schemas_1.dataResponseSchema)(_UserBaseSchema.pick({
    id: true,
    phone_number: true,
    display_name: true,
    email: true,
    preferred_language: true,
    user_role: true,
    created_at: true,
}));
// PATCH /profile - Body
exports.updateProfileBodySchema = zod_1.z.object({
    display_name: zod_1.z.string().max(100).nullable().optional(),
    preferred_language: exports.LanguageSchema.optional(),
});
// PATCH /profile - Response
exports.updateProfileResponseSchema = (0, api_schemas_1.dataResponseSchema)(_UserBaseSchema.pick({
    id: true,
    display_name: true,
    preferred_language: true,
    updated_at: true,
}));
// GET /profile/settings - Response
exports.getSettingsResponseSchema = (0, api_schemas_1.dataResponseSchema)(_UserBaseSchema.pick({
    preferred_language: true,
}));
// PATCH /profile/settings - Body
exports.updateSettingsBodySchema = zod_1.z.object({
    preferred_language: exports.LanguageSchema.optional(),
});
// PATCH /profile/settings - Response
exports.updateSettingsResponseSchema = (0, api_schemas_1.dataResponseSchema)(_UserBaseSchema.pick({
    preferred_language: true,
    updated_at: true,
}));
// ============================================
// USER MERCHANTS API SCHEMAS
// ============================================
// User's merchant membership item (matches frontend transformer expectations)
const _UserMerchantItemSchema = zod_1.z.object({
    merchant_id: zod_1.z.number().int().positive(),
    merchant_name: zod_1.z.string().max(255),
    merchant_description: zod_1.z.string().max(1000).nullable(),
    logo_url: zod_1.z.string().max(500).nullable(),
    member_role: membership_schemas_1.MerchantRoleSchema,
    member_since: timestamp_schemas_1.timestampIsoSchema,
    is_active: zod_1.z.boolean(),
    credit_balance: common_schemas_1.moneyAmountSchema.default(0),
    available_points: zod_1.z.number().default(0),
});
// GET /profile/merchants - Response
exports.getUserMerchantsResponseSchema = (0, api_schemas_1.dataResponseSchema)(zod_1.z.array(_UserMerchantItemSchema));
// DELETE /profile - Response (account deletion)
exports.deleteAccountResponseSchema = (0, api_schemas_1.dataResponseSchema)(zod_1.z.object({
    success: zod_1.z.boolean(),
}));
// Export entity schema for type inference
exports.UserMerchantItemSchema = _UserMerchantItemSchema;
